import type { ResourceDocument, SyncOperation } from '../domain/resources';

export interface LocalDatabase {
  readonly kind: 'indexeddb' | 'native-sqlite' | 'memory';
  initialize(): Promise<void>;
  listResources(): Promise<ResourceDocument[]>;
  putResource(resource: ResourceDocument): Promise<void>;
  commit(resource: ResourceDocument, operations: SyncOperation[]): Promise<void>;
  listOperations(): Promise<SyncOperation[]>;
  putOperations(operations: SyncOperation[]): Promise<void>;
  deleteOperations(ids: string[]): Promise<void>;
  getMetadata(key: string): Promise<string | null>;
  setMetadata(key: string, value: string): Promise<void>;
  flush(): Promise<void>;
}

export class MemoryDatabase implements LocalDatabase {
  readonly kind = 'memory' as const;
  private resources = new Map<string, ResourceDocument>();
  private operations = new Map<string, SyncOperation>();
  private metadata = new Map<string, string>();

  async initialize(): Promise<void> {}

  async listResources(): Promise<ResourceDocument[]> {
    return [...this.resources.values()].map(value => structuredClone(value));
  }

  async putResource(resource: ResourceDocument): Promise<void> {
    this.resources.set(resource.type, structuredClone(resource));
  }

  async commit(resource: ResourceDocument, operations: SyncOperation[]): Promise<void> {
    this.resources.set(resource.type, structuredClone(resource));
    operations.forEach(operation => this.operations.set(operation.id, structuredClone(operation)));
  }

  async listOperations(): Promise<SyncOperation[]> {
    return [...this.operations.values()]
      .map(value => structuredClone(value))
      .sort(compareOperations);
  }

  async putOperations(operations: SyncOperation[]): Promise<void> {
    operations.forEach(operation => this.operations.set(operation.id, structuredClone(operation)));
  }

  async deleteOperations(ids: string[]): Promise<void> {
    ids.forEach(id => this.operations.delete(id));
  }

  async getMetadata(key: string): Promise<string | null> {
    return this.metadata.get(key) ?? null;
  }

  async setMetadata(key: string, value: string): Promise<void> {
    this.metadata.set(key, value);
  }

  async flush(): Promise<void> {}
}

export function compareOperations(left: SyncOperation, right: SyncOperation): number {
  return left.createdAt.localeCompare(right.createdAt)
    || Number(left.localOrder || 0) - Number(right.localOrder || 0)
    || left.id.localeCompare(right.id);
}
